import { documentDepartmentLabel, documentHomeCopy } from "@/lib/ui/document-home";
import { documentCollectionCopy, documentCollectionLabel } from "@/lib/ui/document-collection";

export type DocumentListView = "all" | "mine";

/** Catalog default: managers see the org list; others start from their uploads. */
export function defaultDocumentListView(canSeeAll: boolean): DocumentListView {
  return canSeeAll ? "all" : "mine";
}

/** Distinct departments before the department filter appears for regular users. */
export const DOCUMENT_DEPARTMENT_FILTER_MIN = 2;

export function showDocumentDepartmentFilter(params: {
  filters: ReadonlyArray<{ id: string }>;
  canAssignAnyDepartment: boolean;
}): boolean {
  const departments = params.filters.filter(
    (item) => item.id !== "all" && item.id !== "none",
  ).length;
  if (departments === 0) {
    return false;
  }
  if (params.canAssignAnyDepartment) {
    return true;
  }
  return departments >= DOCUMENT_DEPARTMENT_FILTER_MIN;
}
export type DocumentListDepartmentFilter = "all" | "none" | string;
export type DocumentListCollectionFilter = "all" | "none" | string;
export type DocumentListSortKey =
  | "title"
  | "version"
  | "department"
  | "updated"
  | "status";
export type DocumentListSortDir = "asc" | "desc";
export type DocumentListSort = {
  key: DocumentListSortKey;
  dir: DocumentListSortDir;
};

const STATUS_RANK: Record<string, number> = {
  READY: 0,
  PROCESSING: 1,
  FAILED: 2,
};

export const documentListCopy = {
  title: "Документы",
  upload: "Загрузить",
  uploadPending: "Загружаем…",
  stepFile: "Файл",
  stepAccess: "Кто увидит",
  dropTitle: "Перетащите файлы сюда",
  dropHint: "или выберите на компьютере · PDF, DOCX, TXT, MD · до 30 МБ",
  dropPaste: "Можно вставить текст из буфера (Ctrl+V)",
  dropPick: "Выбрать файлы",
  removeFile: "Убрать",
  tooManyFiles: "Не больше 10 файлов за раз",
  noteLabel: "Заметка",
  uploadOf: (done: number, total: number) => `Загружаем ${done} из ${total}…`,
  uploadPartial: (ok: number, fail: number) =>
    `Загружено: ${ok}. Не удалось: ${fail}.`,
  searchLabel: "Найти документ",
  searchPlaceholder: "Название или владелец",
  viewAll: "Все",
  viewMine: "Мои",
  filterAll: "Все",
  filterNoDepartment: documentHomeCopy.noDepartment,
  departmentFilter: documentHomeCopy.departmentLabel,
  collectionFilter: documentCollectionCopy.label,
  filterNoCollection: documentCollectionCopy.none,
  columnTitle: "Название",
  columnVersion: "Версия",
  columnDepartment: documentHomeCopy.departmentLabel,
  columnUpdated: "Обновлён",
  columnStatus: "Статус",
  columnIndex: "Индекс",
  emptyFilterTitle: "Ничего не найдено",
  emptyFilterDescription: "Измените запрос или сбросьте фильтр.",
  moreFilters: "Фильтры",
} as const;

export const DOCUMENT_PAGE_SIZE = 10;
export const DOCUMENT_PAGE_SIZES = [10, 20, 50] as const;

export function documentSelectedLabel(count: number): string {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) {
    return `Выбрано: ${count} документов`;
  }
  if (n1 === 1) {
    return `Выбран: ${count} документ`;
  }
  if (n1 >= 2 && n1 <= 4) {
    return `Выбрано: ${count} документа`;
  }
  return `Выбрано: ${count} документов`;
}

export function documentDepartmentFilters<
  T extends { departmentId: string | null; departmentName: string | null },
>(rows: T[]): Array<{ id: DocumentListDepartmentFilter; label: string }> {
  const seen = new Map<string, string>();
  let hasNone = false;
  for (const row of rows) {
    if (!row.departmentId) {
      hasNone = true;
      continue;
    }
    if (!seen.has(row.departmentId)) {
      seen.set(row.departmentId, documentDepartmentLabel(row.departmentName));
    }
  }
  const options: Array<{ id: DocumentListDepartmentFilter; label: string }> = [
    { id: "all", label: documentListCopy.filterAll },
  ];
  for (const [id, label] of seen) {
    options.push({ id, label });
  }
  if (hasNone && seen.size > 0) {
    options.push({
      id: "none",
      label: documentListCopy.filterNoDepartment,
    });
  }
  return options;
}

export function documentCollectionFilters<
  T extends { collectionId: string | null; collectionName: string | null },
>(rows: T[]): Array<{ id: DocumentListCollectionFilter; label: string }> {
  const seen = new Map<string, string>();
  let hasNone = false;
  for (const row of rows) {
    if (!row.collectionId) {
      hasNone = true;
      continue;
    }
    if (!seen.has(row.collectionId)) {
      seen.set(row.collectionId, documentCollectionLabel(row.collectionName));
    }
  }
  const options: Array<{ id: DocumentListCollectionFilter; label: string }> = [
    { id: "all", label: documentListCopy.filterAll },
  ];
  for (const [id, label] of seen) {
    options.push({ id, label });
  }
  if (hasNone && seen.size > 0) {
    options.push({
      id: "none",
      label: documentListCopy.filterNoCollection,
    });
  }
  return options;
}

export function filterDocumentRows<
  T extends {
    title: string;
    type: string;
    owner: string;
    status: string;
    mine?: boolean;
    departmentName?: string | null;
    departmentId?: string | null;
    collectionName?: string | null;
    collectionId?: string | null;
    version?: number | null;
  },
>(
  rows: T[],
  query: string,
  view: DocumentListView = "all",
  department: DocumentListDepartmentFilter = "all",
  collection: DocumentListCollectionFilter = "all",
): T[] {
  const needle = query.trim().toLowerCase();
  return rows.filter((row) => {
    if (view === "mine" && !row.mine) {
      return false;
    }
    if (department === "none" && row.departmentId) {
      return false;
    }
    if (
      department !== "all" &&
      department !== "none" &&
      row.departmentId !== department
    ) {
      return false;
    }
    if (collection === "none" && row.collectionId) {
      return false;
    }
    if (
      collection !== "all" &&
      collection !== "none" &&
      row.collectionId !== collection
    ) {
      return false;
    }
    if (!needle) {
      return true;
    }
    return [
      row.title,
      row.type,
      row.owner,
      row.departmentName ?? "",
      row.collectionName ?? "",
      row.version != null ? String(row.version) : "",
    ].some((value) => value.toLowerCase().includes(needle));
  });
}

export function nextDocumentSort(
  current: DocumentListSort,
  key: DocumentListSortKey,
): DocumentListSort {
  if (current.key === key) {
    return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: key === "updated" ? "desc" : "asc" };
}

export function sortDocumentRows<
  T extends {
    title: string;
    status: string;
    departmentName?: string | null;
    updatedAt?: string;
    version?: number | null;
  },
>(rows: T[], sort: DocumentListSort): T[] {
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    if (sort.key === "title") {
      return sign * left.title.localeCompare(right.title, "ru");
    }
    if (sort.key === "version") {
      return sign * ((left.version ?? 0) - (right.version ?? 0));
    }
    if (sort.key === "department") {
      return (
        sign *
        (left.departmentName ?? "").localeCompare(
          right.departmentName ?? "",
          "ru",
        )
      );
    }
    if (sort.key === "status") {
      return (
        sign *
        ((STATUS_RANK[left.status] ?? 9) - (STATUS_RANK[right.status] ?? 9))
      );
    }
    return (
      sign *
      (left.updatedAt ?? "").localeCompare(right.updatedAt ?? "")
    );
  });
}
